import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { figureService, type FigureMeta } from '../../services/figureService';
import { useFigureBlob } from '../../hooks/useFigureBlob';

interface FigureInsertModalProps {
  projectId: string;
  onInsert: (meta: FigureMeta) => void;
  onClose: () => void;
}

type Tab = 'upload' | 'gallery';

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

/** Renders a figure thumbnail using an authenticated blob fetch (no token in URL). */
const FigureThumbnail: React.FC<{ projectId: string; figureId: string; alt: string }> = ({
  projectId, figureId, alt,
}) => {
  const { blobUrl, error } = useFigureBlob(projectId, figureId);
  if (error) {
    return (
      <div className="w-full h-20 flex items-center justify-center text-xs text-red-500 bg-red-50 rounded">
        Error
      </div>
    );
  }
  if (!blobUrl) {
    return <div className="w-full h-20 bg-slate-100 animate-pulse rounded" />;
  }
  return <img src={blobUrl} alt={alt} className="w-full h-20 object-contain bg-slate-50 rounded" />;
};

const FigureInsertModal: React.FC<FigureInsertModalProps> = ({ projectId, onInsert, onClose }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('upload');

  // Upload tab state
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Gallery tab state
  const [figures, setFigures] = useState<FigureMeta[] | null>(null);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState<string | null>(null);

  const loadGallery = async () => {
    setGalleryLoading(true);
    setGalleryError(null);
    try {
      const list = await figureService.list(projectId);
      setFigures(list);
    } catch {
      setGalleryError(t('figures.gallery.loadError', 'Failed to load figures'));
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab);
    if (newTab === 'gallery' && figures === null) loadGallery();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setUploadError(null);
    if (!selected) { setFile(null); return; }
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setUploadError(t('figures.upload.typeError', 'Only PNG and JPEG files are accepted'));
      setFile(null); return;
    }
    if (selected.size > MAX_BYTES) {
      setUploadError(t('figures.upload.sizeError', 'File must be smaller than 10 MB'));
      setFile(null); return;
    }
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const meta = await figureService.upload(projectId, file, caption || undefined, label || undefined);
      onInsert(meta);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d: { msg?: string }) => d.msg ?? JSON.stringify(d)).join('; ')
        : typeof detail === 'string'
          ? detail
          : t('figures.upload.error', 'Upload failed');
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[520px] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <span className="text-sm font-semibold text-slate-700">
            {t('figures.insert.title', 'Insert Figure')}
          </span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">&times;</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {(['upload', 'gallery'] as Tab[]).map(t_ => (
            <button
              key={t_}
              onClick={() => handleTabChange(t_)}
              className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                tab === t_
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t_ === 'upload'
                ? t('figures.insert.tabUpload', 'Upload New')
                : t('figures.insert.tabGallery', 'Gallery')}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'upload' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  {t('figures.upload.fileLabel', 'Image file (PNG or JPEG, max 10 MB)')}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleFileChange}
                  className="text-xs w-full"
                />
                {uploadError && <p className="text-xs text-red-600 mt-1">{uploadError}</p>}
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-slate-600 mb-1">
                    {t('figures.upload.labelField', 'Label (e.g. FIG. 1)')}
                  </label>
                  <input
                    type="text"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    maxLength={50}
                    placeholder="FIG. 1"
                    className="w-full border border-slate-200 rounded px-2 py-1 text-xs outline-none focus:border-blue-400"
                  />
                </div>
                <div className="flex-[2]">
                  <label className="block text-xs text-slate-600 mb-1">
                    {t('figures.upload.captionField', 'Caption (optional)')}
                  </label>
                  <input
                    type="text"
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    maxLength={500}
                    placeholder={t('figures.upload.captionPlaceholder', 'Brief description of the figure')}
                    className="w-full border border-slate-200 rounded px-2 py-1 text-xs outline-none focus:border-blue-400"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="text-xs px-4 py-1.5 rounded bg-blue-600 text-white font-semibold disabled:opacity-40"
                >
                  {uploading
                    ? t('figures.upload.uploading', 'Uploading...')
                    : t('figures.upload.button', 'Upload & Insert')}
                </button>
              </div>
            </div>
          )}

          {tab === 'gallery' && (
            <div>
              {galleryLoading && (
                <p className="text-xs text-slate-400">{t('common.loading', 'Loading...')}</p>
              )}
              {galleryError && <p className="text-xs text-red-600">{galleryError}</p>}
              {figures !== null && figures.length === 0 && (
                <p className="text-xs text-slate-400">
                  {t('figures.gallery.empty', 'No figures uploaded for this project yet.')}
                </p>
              )}
              {figures !== null && figures.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {figures.map(fig => (
                    <button
                      key={fig.id}
                      onClick={() => onInsert(fig)}
                      className="border border-slate-200 rounded p-1.5 hover:border-blue-400 hover:bg-blue-50 text-left"
                    >
                      <FigureThumbnail
                        projectId={projectId}
                        figureId={fig.id}
                        alt={fig.figure_label || fig.caption || 'Figure'}
                      />
                      <p className="text-[10px] text-slate-600 mt-1 truncate">
                        {fig.figure_label || fig.caption || fig.id.slice(0, 8)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FigureInsertModal;
